import StarRating from "@/components/tuw/hire/StarRating"
import { Avatar } from "@/components/tuw/Avatar"
import { Button } from "@/components/ui/button"
import { DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { BRIEF_WORDS } from "@/lib/openHouse/brief"
import { money } from "@/lib/openHouse/bookWritingSteps"
import { REQUEST_WORDS, acceptPrice, requestCopy } from "@/lib/openHouse/requests"

// An Open House request in the requests dialog (FreelancerRequestsDialog):
// its facts, the price line beside Accept, and the decline confirmation.

// The card's facts (new-flow-wording §13, §24): the
// freelancer's rating, when they requested and, on a project open to every
// writer level, that writer's price for it (OrderStatusMapping#request_items
// `openHouse`, computed server-side; absent when it is not exact).
export function RequestFacts({ request }) {
  const facts = request.openHouse
  if (!facts) return null

  return (
    <dl className="mt-3 grid grid-cols-3 gap-3 text-[12.5px] max-[560px]:grid-cols-2">
      <Fact label={REQUEST_WORDS.rating}>
        <StarRating rating={facts.rating} showCount={false} />
      </Fact>
      <Fact label={REQUEST_WORDS.requestedOn}>{facts.requestedOn}</Fact>
      {facts.price != null && (
        <Fact label={requestCopy(request.brief?.kind).requestPrice ?? REQUEST_WORDS.requestPrice}>
          <span className="font-display font-bold">{money(facts.price)}</span>
        </Fact>
      )}
    </dl>
  )
}

// The line beside Accept on a priced request: what accepting commits to.
export function AcceptPriceNote({ request }) {
  const price = request.openHouse?.price
  if (price == null) return null

  return (
    <p className="w-full text-[13px] text-foreground">
      <span className="font-semibold">{acceptPrice(request.brief?.kind, money(price))}</span>{" "}
      <span className="text-ink-3">{REQUEST_WORDS.taxAtCheckout}</span>
    </p>
  )
}

function Fact({ label, children }) {
  return (
    <div className="min-w-0">
      <dt className="mb-0.5 text-[11px] font-bold tracking-wide text-ink-3 uppercase">{label}</dt>
      <dd className="text-foreground">{children}</dd>
    </div>
  )
}

// §8: which request is being declined, confirmed before it is.
export function DeclineRequestConfirm({ request, onBack, onDecline }) {
  const copy = requestCopy(request.brief?.kind)

  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display">{copy.declineH}</DialogTitle>
        <DialogDescription>{copy.declineB}</DialogDescription>
      </DialogHeader>

      <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
        <Avatar initials={request.freelancer.initials} name={request.freelancer.name} size={32} />
        <div className="min-w-0">
          <div className="font-display text-sm font-bold">{request.freelancer.name}</div>
          <div className="text-xs text-ink-3">{copy.freelancer}</div>
        </div>
      </div>

      <div className="flex flex-wrap justify-end gap-2 max-[560px]:flex-col-reverse max-[560px]:items-stretch">
        <Button variant="outline" onClick={onBack}>
          {BRIEF_WORDS.backToRequests}
        </Button>
        <Button className="font-semibold" onClick={onDecline}>
          {REQUEST_WORDS.declineCta}
        </Button>
      </div>
    </>
  )
}
